import { OAuth2 } from '@backstage/core-app-api';
import {
  alertApiRef,
  AnyApiFactory,
  ApiRef,
  BackstageIdentityApi,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  githubAuthApiRef,
  identityApiRef,
  OAuthApi,
  oauthRequestApiRef,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
  storageApiRef,
} from '@backstage/core-plugin-api';
import { ScmIntegrations } from '@backstage/integration';
import {
  ScmAuth,
  scmAuthApiRef,
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';
import {
  DefaultShortcutsApi,
  shortcutsApiRef,
} from '@backstage-community/plugin-shortcuts';
import { UserSettingsStorage } from '@backstage/plugin-user-settings';
import {
  appStandardInsightsApiRef,
  AppStandardInsightsApiClient,
} from '@mercedes-benz/backstage-plugin-app-standard-insights';

// `ProfileInfoApi & BackstageIdentityApi & SessionApi` are required for sign-in
// Include `OAuthApi & OpenIdConnectApi` only if applicable
export const oidcAuthRef: ApiRef<
  OAuthApi &
    OpenIdConnectApi &
    ProfileInfoApi &
    BackstageIdentityApi &
    SessionApi
> = createApiRef({
  id: 'internal.auth.oidc',
});

// Minimal ToastApi types — mirrors @backstage/frontend-plugin-api's ToastApi (introduced in v1.49.0)
type ToastApiMessage = {
  title: string;
  description?: string;
  status?: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
  timeout?: number;
  links?: Array<{ label: string; href: string }>;
};
type ToastApi = { post(toast: ToastApiMessage): { close(): void } };
const toastApiRef = createApiRef<ToastApi>({ id: 'core.toast' });

function toastSeverity(
  status: ToastApiMessage['status'],
): 'error' | 'info' | 'success' | 'warning' {
  if (status === 'danger') return 'error';
  if (status === 'neutral' || status === undefined) return 'info';
  return status;
}

export const apis: AnyApiFactory[] = [
  // see: https://github.com/backstage/backstage/blob/master/plugins/catalog-import/CHANGELOG.md#070
  createApiFactory({
    api: scmAuthApiRef,
    deps: {
      configApi: configApiRef,
      gheAuthApi: createApiRef<
        OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
      >({
        id: 'core.auth.github',
      }),
      githubAuthApi: githubAuthApiRef,
    },
    factory: ({ githubAuthApi, gheAuthApi, configApi }) =>
      ScmAuth.merge(
        ScmAuth.forGithub(githubAuthApi),
        ScmAuth.forGithub(gheAuthApi, {
          host: ScmIntegrations.fromConfig(configApi)
            .github.list()
            .find(({ config }) => config.host.includes('mercedes-benz'))!.config
            .host,
        }),
      ),
  }),
  createApiFactory({
    api: storageApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      errorApi: errorApiRef,
      fetchApi: fetchApiRef,
      identityApi: identityApiRef,
    },
    factory: deps => UserSettingsStorage.create(deps),
  }),
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  createApiFactory({
    api: shortcutsApiRef,
    deps: { storageApi: storageApiRef },
    factory: ({ storageApi }) =>
      new DefaultShortcutsApi(storageApi.forBucket('shortcuts')),
  }),
  // oauth2
  createApiFactory({
    api: oidcAuthRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        configApi,
        discoveryApi,
        oauthRequestApi,
        provider: {
          id: 'oidc',
          title: 'Single Sign On',
          icon: () => null,
        },
        environment: configApi.getOptionalString('auth.environment'),
      }),
  }),
  // AppStandardInsights API
  createApiFactory({
    api: appStandardInsightsApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      fetchApi: fetchApiRef,
    },
    factory: ({ discoveryApi, fetchApi }) =>
      new AppStandardInsightsApiClient({ discoveryApi, fetchApi }),
  }),
  // ToastApi — bridges newer community plugins that call toastApiRef to the legacy AlertApi
  createApiFactory({
    api: toastApiRef,
    deps: { alertApi: alertApiRef },
    factory: ({ alertApi }) => ({
      post: (toast: ToastApiMessage) => {
        const linksSuffix =
          toast.links && toast.links.length > 0
            ? ` — ${toast.links.map(l => [l.label, l.href].join(': ')).join(' | ')}`
            : '';
        const base = toast.description
          ? `${toast.title}: ${toast.description}`
          : toast.title;
        alertApi.post({
          message: `${base}${linksSuffix}`,
          severity: toastSeverity(toast.status),
          display:
            toast.timeout !== undefined && toast.timeout > 0
              ? 'transient'
              : 'permanent',
        });
        return { close: () => {} };
      },
    }),
  }),
];
